import React, { useState } from 'react';
import SbButton from '../Button';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '../../store';
import { UpdateShipmentStatusPostData } from '../../store/reducers/ShipmentHistory/Type';
import { Typography, useTheme, Modal, Box, TextField, Fade, Backdrop, InputAdornment } from '@mui/material';
import { cancelShipment } from '@Reducers/ShipmentHistory';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import { isActionAllowed } from '../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
interface CancelShipmentProps {
  disabled?: boolean;
  isVoidMode?: boolean;
}

function CancelShipment({ disabled = false, isVoidMode = false }: CancelShipmentProps) {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();

  const paramQuery = useParams();
  const shipmentNumber = paramQuery?.id || paramQuery?.sn;
  const _userId = useSelector((state: RootStateType) => state.loginUser.profile?._id) || '';

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isCancelShipmentAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.cancel_shipment,
    PERFORM_ACTION.write,
  );

  const isVoidShipmentAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.void_shipment,
    PERFORM_ACTION.write,
  );

  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const SbActionButtonStyle = {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.text.dark,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[4],
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  };

  function handleOpen() {
    if (!isCancelShipmentAllowed && !isVoidShipmentAllowed) return;
    setOpen(true);
  }

  function handleClose() {
    setMsg('');
    setError('');
    setOpen(false);
  }

  function handleSubmit() {
    if (!shipmentNumber) return;

    if (!msg.trim()) {
      setError('Message is required');
      return;
    }

    const postData: UpdateShipmentStatusPostData = {
      _userId,
      shipmentNumbers: [shipmentNumber],
      location: '-',
      msg,
      status: isVoidMode ? 'void' : 'cancelled',
    };

    dispatch(cancelShipment(postData));
    handleClose();
  }

  const modalTitle = isVoidMode ? 'Void Shipment' : 'Cancel Order';
  const modalDescription = isVoidMode
    ? 'Are you sure you want to void this shipment? This action cannot be undone.'
    : 'Are you sure you want to cancel this order? This action cannot be undone.';
  const buttonIcon = <CloseIcon fontSize="small" />;
  const buttonText = isVoidMode ? 'Void Shipment' : 'Cancel Order';

  return (
    <div
      className={`${
        !disabled && (isCancelShipmentAllowed || isVoidShipmentAllowed) ? 'cursor-pointer' : 'cursor-not-allowed'
      }`}
    >
      <SbButton
        containerClass="py-2 px-4 normal-case rounded-lg"
        variant="contained"
        color="primary"
        size="medium"
        style={SbActionButtonStyle}
        onClick={handleOpen}
        startIcon={buttonIcon}
        disabled={disabled || (!isCancelShipmentAllowed && !isVoidShipmentAllowed)}
      >
        <Typography className="font-semibold text-sm tracking-wide">{buttonText}</Typography>
      </SbButton>

      {/* Cancel Shipment Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 300,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)' },
        }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute' as const,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 480,
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 24,
              p: 4,
              outline: 'none',
              animation: 'slideIn 0.3s ease-out',
              '@keyframes slideIn': {
                from: {
                  opacity: 0,
                  transform: 'translate(-50%, -40%)',
                },
                to: {
                  opacity: 1,
                  transform: 'translate(-50%, -50%)',
                },
              },
            }}
          >
            {/* Header with Icon */}
            <Box display="flex" alignItems="center" mb={3}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}
              >
                {isVoidMode ? (
                  <WarningAmberIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                ) : (
                  <InfoIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />
                )}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600} color="text.primary">
                  {modalTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {modalDescription}
                </Typography>
              </Box>
            </Box>

            {/* Shipment Info */}
            {shipmentNumber && (
              <Box
                sx={{
                  backgroundColor: theme.palette.grey[50],
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                  border: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Shipment Number
                </Typography>
                <Typography variant="body1" fontWeight={600} color="text.primary">
                  {shipmentNumber}
                </Typography>
              </Box>
            )}

            {/* Reason Input */}
            <Box mb={3}>
              <Typography variant="subtitle2" color="text.primary" mb={1} fontWeight={500}>
                Reason for {isVoidMode ? 'voiding' : 'cancellation'}{' '}
                <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder={`Enter detailed reason for ${isVoidMode ? 'voiding' : 'cancelling'} this shipment...`}
                value={msg}
                onChange={e => {
                  setMsg(e.target.value);
                  if (error) setError('');
                }}
                error={!!error}
                helperText={error || 'Please provide a clear reason for this action'}
                multiline
                minRows={4}
                maxRows={6}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                    color: error ? theme.palette.error.main : theme.palette.text.secondary,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InfoIcon sx={{ color: theme.palette.grey[400], fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <SbButton
                variant="outlined"
                color="secondary"
                onClick={handleClose}
                containerClass="px-4 py-2 rounded-lg"
                style={{
                  borderColor: theme.palette.grey[300],
                  color: theme.palette.text.secondary,
                }}
              >
                Close
              </SbButton>
              <SbButton
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                containerClass="px-6 py-2 rounded-lg"
                style={{
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                }}
                startIcon={buttonIcon}
              >
                Confirm {isVoidMode ? 'Void' : 'Cancel'}
              </SbButton>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
}

export default CancelShipment;
